import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useResponsive } from '@/hooks/use-responsive';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloError } from '@apollo/client';
import axios from 'axios';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getLogoSpecifications } from '.';
import { SettingsContainer, SettingsSection } from '../..';
import { getCacheBustedFileUrl } from '../../utils';
import { IFirmSettings } from '../templatesSettings/types';

const emailLogoFileExtensions = ['PNG', 'WEBP', 'JPEG', 'JPG'];

interface ThemeUploadLogoProps {
  currentRow: IFirmSettings | null;
  handleClose: () => void;
}

export const ThemeUploadLogo = ({ currentRow, handleClose }: ThemeUploadLogoProps) => {
  const { isMobile } = useResponsive();
  const { data: customizationsData, updateCustomizationsDataByKey } = useCustomizationsData();

  const [addAttachmentError, setAddAttachmentError] = useState<ApolloError | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'not-supported' });
  const [isFileToDelete, setIsFileToDelete] = useState(false);
  const [fileExtensions, setFileExtensions] = useState<string[]>([]);
  const [filePath, setFilePath] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentRow) return;

    switch (currentRow.key) {
      case 'loginLogo':
        setFileExtensions(['SVG']);
        setFilePath('logos/login-logo');
        break;
      case 'navbarLogo':
        setFileExtensions(['SVG']);
        setFilePath('logos/navbar-logo');
        break;
      case 'navbarSimpleLogo':
        setFileExtensions(['SVG']);
        setFilePath('logos/navbar-simple-logo');
        break;
      case 'emailLogo':
        setFileExtensions(emailLogoFileExtensions);
        setFilePath('logos/email-logo');
        break;
      default:
        break;
    }
  }, [currentRow]);

  useEffect(() => {
    if (!currentRow) return;

    setFileUrl(currentRow?.value || '');
  }, [currentRow]);

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;
    setDrag(false);

    if (fileExtensions.includes(uploadFiles[0]?.name.split('.').pop()?.toUpperCase() ?? '')) {
      setFile(uploadFiles[0]);
      setFileUrl(URL.createObjectURL(uploadFiles[0]));
      return;
    }
    setModalWindow({ isOpen: true, type: 'not-supported' });
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;

    if (uploadFiles && fileExtensions.includes(uploadFiles[0]?.name.split('.').pop()?.toUpperCase() ?? '')) {
      setFile(uploadFiles[0]);
      setFileUrl(URL.createObjectURL(uploadFiles[0]));
      return;
    }
    setModalWindow({ isOpen: true, type: 'not-supported' });
  };

  const openLogoImage = (url: string) => {
    window.open(url, '_blank');
  };

  const deletePreviewItem = () => {
    setFile(null);
    setFileUrl('');
    if (currentRow?.value) {
      setIsFileToDelete(true);
    }
  };

  const handleAttachmentError = (error: ApolloError) => {
    console.log(error, 'error');
    if (error.message.includes('duplicate key value violates unique constraint') || error.message.includes('Asset key already exists')) {
      setAddAttachmentError(error);
      setFile(null);
      setFileUrl(currentRow?.value || '');
    }
  };

  const uploadCatalogBannerImage = async (file: File) => {
    try {
      setLoading(true);

      if (isFileToDelete && !file) {
        const currentFileExtension = currentRow?.value?.split('.')?.pop()?.toLowerCase();

        await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, {
          data: {
            filePath: `${filePath}.${currentFileExtension}`
          },
          headers: {
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        const { data: updatedCustomizationData } = await axios.post(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/system/update-customization`,
          {
            key: 'logos',
            value: JSON.stringify({
              ...(customizationsData?.logos ?? {}),
              [currentRow?.key]: ''
            })
          },
          {
            headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );

        if (updatedCustomizationData?.value) {
          updateCustomizationsDataByKey('logos', JSON.parse(updatedCustomizationData.value));
        }
      }

      if (file) {
        const currentFileExtension = currentRow?.value?.split('.')?.pop()?.toLowerCase();
        const newFileExtension = file?.name?.split('.')?.pop()?.toLowerCase();
        if (currentRow?.value && currentFileExtension !== newFileExtension) {
          await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, {
            data: {
              filePath: `${filePath}.${currentFileExtension}`
            },
            headers: {
              authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('filePath', `${filePath}.${newFileExtension}`);
        formData.append('metadata', JSON.stringify({}));

        const { data } = await axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        const { data: updatedCustomizationData } = await axios.post(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/system/update-customization`,
          {
            key: 'logos',
            value: JSON.stringify({
              ...(customizationsData?.logos ?? {}),
              [currentRow?.key]: data?.url ?? ''
            })
          },
          {
            headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );

        if (updatedCustomizationData?.value) {
          updateCustomizationsDataByKey('logos', JSON.parse(updatedCustomizationData.value));
        }
      }

      handleClose();
    } catch (error) {
      console.log(error, 'error');
      handleAttachmentError(error as ApolloError);
    } finally {
      setLoading(false);
    }
  };

  if (!currentRow) return <></>;

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'not-supported' && (
        <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ ...modalWindow, isOpen: false })} />
      )}
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Firm Settings" />} />
        <PageTitle title={currentRow?.fieldName ?? ''} />
      </MainWrap>
      <MainWrap>
        {addAttachmentError && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="File name already exists"
            description="In order to upload your file please give it a unique name."
            bgColor={themeVar('context.error')}
          />
        )}
        <SettingsContainer>
          <SettingsSection isMobile={isMobile}>
            <LogoSpecifications>
              Logo Specifications:
              <LogoSpecificationsUnorderedList>
                {getLogoSpecifications(currentRow?.key)?.map((specification) => <li key={specification}>{specification}</li>)}
              </LogoSpecificationsUnorderedList>
            </LogoSpecifications>
            <>
              {fileUrl && (
                <FileUpload
                  fileName="Logo"
                  imageSrc={getCacheBustedFileUrl(fileUrl)}
                  type="image"
                  openFile={() => openLogoImage(getCacheBustedFileUrl(fileUrl))}
                  deleteFile={deletePreviewItem}
                />
              )}
              <DropFileArea
                id="catalogBannerLogo"
                fileExtensions={fileExtensions}
                handleUploadFile={handleUploadFile}
                onDropHandler={onDropHandler}
                drag={drag}
                setDrag={setDrag}
                maxSize={10}
                withIcon
              />
            </>
          </SettingsSection>
          <FormFooter
            onCancel={handleClose}
            onSave={() => uploadCatalogBannerImage(file)}
            disableSaveButton={false}
            showRequiredFieldsWarning={false}
          />
        </SettingsContainer>
      </MainWrap>
    </>
  );
};

const LogoSpecifications = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  margin-bottom: 20px;
  color: ${themeVar('font.base')};
`;

const LogoSpecificationsUnorderedList = styled.ul`
  margin-left: 25px;
  list-style-type: square;
`;
